import { roles } from '../data/roles';
import { BpsAdjustment, CommissionStep, LineItem, Loan, Officer, Period, Role } from '../types/commission';
import { CommissionTotal, capTotal, lineCommission, payingStep } from './commission';
import { buildLoans, unitsFor } from './production';

export const productionTiers = roles.filter((role) => role.group === 'Production');

export interface OfficerPeriodResult {
  officer: Officer;
  role: Role;
  period: Period;
  units: number;
  loans: Loan[];
  lineItems: LineItem[];
  volume: number;
  scorecardMet: boolean;
  step: CommissionStep;
  activeStepIndex: number;
  /** Rate paid on every loan without an individual override (bps roles only). */
  periodRate: number | null;
  adjustment: BpsAdjustment | null;
  overrideCount: number;
  totals: CommissionTotal;
}

export function adjustmentKey(officerId: string, periodId: string): string {
  return `${officerId}:${periodId}`;
}

export function roleFor(officer: Officer): Role {
  return roles.find((role) => role.id === officer.roleId) ?? roles[0];
}

/** Everything a user is paid for one pay period, including admin adjustments and overrides. */
export function computeOfficerPeriod(
  officer: Officer,
  period: Period,
  adjustments: Record<string, BpsAdjustment>,
  overrides: Record<string, BpsAdjustment>,
): OfficerPeriodResult {
  const role = roleFor(officer);
  const isBps = role.model === 'bps';
  const units = unitsFor(officer, role, period.id);
  const loans = buildLoans(period, officer.id, units, role.group === 'Production');
  const adjustment = adjustments[adjustmentKey(officer.id, period.id)] ?? null;
  const scorecardMet = period.scorecard.points >= period.scorecard.goal;
  const step = payingStep(role, units, scorecardMet);
  const activeStepIndex = Math.max(
    0,
    role.steps.findIndex((item) => item.minUnits === step.minUnits),
  );
  const periodRate = isBps ? (step.totalBps ?? 0) + (adjustment?.bps ?? 0) : null;

  let overrideCount = 0;
  const lineItems: LineItem[] = loans.map((loan) => {
    const override = overrides[loan.id];
    if (override) overrideCount += 1;
    const bps = isBps ? (override?.bps ?? periodRate) : null;
    return { loan, bps, commission: lineCommission(role, step, loan, bps), overrideApplied: Boolean(override) };
  });

  return {
    officer,
    role,
    period,
    units,
    loans,
    lineItems,
    volume: loans.reduce((sum, loan) => sum + loan.amount, 0),
    scorecardMet,
    step,
    activeStepIndex,
    periodRate,
    adjustment,
    overrideCount,
    totals: capTotal(
      role,
      lineItems.reduce((sum, item) => sum + item.commission, 0),
    ),
  };
}
